import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import CronBuilderBox from "../../../components/CronBuilderBox";
import CronStatusPanel from "../../../components/CronStatusPanel";
import { statisticsApi } from "../../../services/api/omni/statistics";

const ShoppingTableTab = () => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [cronSettings, setCronSettings] = useState({
    cron_expression: "0 3 * * *",
    is_cron_active: false,
    last_run: null,
    next_run: null,
  });

  useEffect(() => {
    loadCronStatus();
  }, []);

  const loadCronStatus = async () => {
    try {
      const response = await statisticsApi.getCronStatus();
      setCronSettings((prev) => ({
        ...prev,
        is_cron_active: response.data.active,
        cron_expression: response.data.expression || prev.cron_expression,
        last_run: response.data.last_run_at,
        next_run: response.data.next_run_at,
      }));
    } catch (err) {
      console.error("Failed to load cron status:", err);
    }
  };

  const handleStartCron = async () => {
    try {
      setLoading(true);
      setError(null);
      await statisticsApi.startCron(cronSettings.cron_expression);
      setSuccess(
        `Cron started / updated with schedule: ${cronSettings.cron_expression}`,
      );
      await loadCronStatus();
    } catch (err) {
      setError(
        "Failed to start/update cron: " +
          (err.response?.data?.detail ?? err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStopCron = async () => {
    try {
      setLoading(true);
      setError(null);
      await statisticsApi.stopCron();
      setSuccess("Cron stopped successfully.");
      await loadCronStatus();
    } catch (err) {
      setError(
        "Failed to stop cron: " + (err.response?.data?.detail ?? err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleManualSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      await statisticsApi.triggerSync();
      setSuccess(
        "Manual sync triggered — data is being imported in the background.",
      );
    } catch (err) {
      setError(
        "Failed to trigger sync: " +
          (err.response?.data?.detail ?? err.message),
      );
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* ── Cron Control Panel ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Header row */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              Shopping Table Import — Schedule
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadCronStatus}
                disabled={loading}
              >
                Refresh
              </Button>
              <Chip
                icon={
                  cronSettings.is_cron_active ? (
                    <CheckCircleIcon />
                  ) : (
                    <StopIcon />
                  )
                }
                label={cronSettings.is_cron_active ? "Running" : "Stopped"}
                color={cronSettings.is_cron_active ? "success" : "error"}
                variant="outlined"
              />
            </Box>
          </Box>

          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Cron generator */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Cron Schedule
              </Typography>
              <CronBuilderBox mb={2}>
                <Cron
                  onChange={(e) =>
                    setCronSettings((prev) => ({
                      ...prev,
                      cron_expression: e,
                    }))
                  }
                  value={cronSettings.cron_expression}
                  showResultText={true}
                  showResultCron={true}
                  locale="en"
                />
              </CronBuilderBox>
            </Box>

            {/* Action buttons */}
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlayIcon />
                  )
                }
                onClick={handleStartCron}
                disabled={loading}
              >
                Start / Update Schedule
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleStopCron}
                disabled={loading || !cronSettings.is_cron_active}
              >
                Stop Schedule
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={
                  syncing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SyncIcon />
                  )
                }
                onClick={handleManualSync}
                disabled={syncing}
              >
                {syncing ? "Importing…" : "Run Import Now"}
              </Button>
            </Box>

            <CronStatusPanel isActive={cronSettings.is_cron_active}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Cron Status
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {cronSettings.is_cron_active ? (
                      <Box
                        component="span"
                        sx={{
                          color: "success.main",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <CheckCircleIcon fontSize="small" /> Active
                      </Box>
                    ) : (
                      <Box
                        component="span"
                        sx={{
                          color: "error.main",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <StopIcon fontSize="small" /> Inactive
                      </Box>
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Schedule
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                    {cronSettings.cron_expression || "Not set"}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Last Run
                  </Typography>
                  <Typography variant="body1">
                    {cronSettings.last_run
                      ? new Date(cronSettings.last_run).toLocaleString("de-DE")
                      : "Never"}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    Next Execution
                  </Typography>
                  <Typography
                    variant="body1"
                    color={
                      cronSettings.is_cron_active
                        ? "textPrimary"
                        : "textSecondary"
                    }
                  >
                    {cronSettings.next_run && cronSettings.is_cron_active
                      ? new Date(cronSettings.next_run).toLocaleString("de-DE")
                      : "—"}
                  </Typography>
                </Box>
              </Box>
            </CronStatusPanel>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ShoppingTableTab;
